import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { HeroBookingWidget } from "@/components/HeroBookingWidget";
import { useLanguage } from "@/context/LanguageContext";
import { useSeo } from "@/lib/useSeo";
import { PAGE_SEO } from "@/data/pageSeo";
import { IMAGES } from "@/data/images";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, Calendar, Car, Users, Shield, Clock, Star, Phone, CreditCard, PlaneTakeoff, MapPin } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const services = [
  { id: "private_driver", img: IMAGES.privateDriver, tag: "From $90", quote: false },
  { id: "limousine", img: IMAGES.limousine, tag: "From $150", quote: false },
  { id: "party_bus", img: IMAGES.transitBus, tag: null, quote: true },
  { id: "transit_bus", img: IMAGES.charterBus, tag: null, quote: true },
];

const trust = [
  { icon: Shield, key: "trust.licensed" },
  { icon: Star, key: "trust.chauffeurs" },
  { icon: Clock, key: "trust.support" },
  { icon: PlaneTakeoff, key: "trust.flight" },
  { icon: CreditCard, key: "trust.secure" },
  { icon: MapPin, key: "trust.montreal" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  useSeo({ ...(PAGE_SEO.home[lang] || PAGE_SEO.home.en), path: PAGE_SEO.home.path, image: PAGE_SEO.home.image, lang });
  const phone = t("hero.phone");

  return (
    <div className="bg-background text-foreground overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.heroLimo} alt="Luxury chauffeured limousine at night in Montréal"
            className="w-full h-full object-cover animate-slow-zoom" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-16 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.p initial="hidden" animate="show" variants={fadeUp}
                className="text-xs tracking-luxe uppercase text-white/60 mb-5">
                {t("hero.eyebrow")}
              </motion.p>
              <motion.h1 initial="hidden" animate="show" variants={fadeUp} transition={{ delay: 0.1 }}
                className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-white">
                {t("hero.title")}
              </motion.h1>
              <motion.p initial="hidden" animate="show" variants={fadeUp} transition={{ delay: 0.2 }}
                className="text-white/70 text-base sm:text-lg max-w-xl mt-6 leading-relaxed">
                {t("hero.subtitle")}
              </motion.p>
              <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ delay: 0.3 }}
                className="flex flex-wrap items-center gap-4 mt-8">
                <a href={`tel:+1${phone.replace(/[^0-9]/g, "")}`} data-testid="hero-call-link">
                  <Button variant="outline"
                    className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 h-12 px-7">
                    <Phone className="h-4 w-4 mr-2" /> {t("hero.call")} · {phone}
                  </Button>
                </a>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}>
              <HeroBookingWidget />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-white/10 bg-card/40" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-4">
            {trust.map((item) => (
              <div key={item.key} className="flex items-center gap-2.5 text-white/70 text-sm">
                <item.icon className="h-4 w-4 flex-shrink-0 text-white" />
                <span>{t(item.key)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service categories */}
      <section id="services" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-xs tracking-luxe uppercase text-muted-foreground mb-4">{t("cat.eyebrow")}</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-white max-w-xl leading-tight">
              {t("cat.title")}
            </h2>
          </div>
          <p className="text-muted-foreground text-base max-w-sm">{t("cat.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, i) => (
            <motion.div key={s.id}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`service-card-${s.id}`}
              className="group relative flex flex-col overflow-hidden rounded-xl border border-white/10 bg-card cursor-pointer hover:border-white/30 transition-colors"
              onClick={() => navigate(`/book?service=${s.id}`)}>
              <div className="relative h-56 overflow-hidden">
                <img src={s.img} alt={t(`svc.${s.id}.title`)}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <span className="absolute top-4 right-4 text-[11px] tracking-luxe uppercase text-white bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-3 py-1">
                  {s.quote ? t("cat.quoteOnly") : (s.tag ? `${t("booking.startingFrom")} ${s.tag.replace("From ", "")}` : t("cat.instant"))}
                </span>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-serif text-2xl text-white mb-2">{t(`svc.${s.id}.title`)}</h3>
                <p className="text-[11px] uppercase tracking-luxe text-white/45 mb-3">{t(`cat.${s.id}.for`)}</p>
                <p className="text-muted-foreground text-sm leading-relaxed mb-5 flex-1">{t(`svc.${s.id}.desc`)}</p>
                <span className="inline-flex items-center text-sm text-white group-hover:gap-3 gap-2 transition-all">
                  {s.quote ? t("cat.quote") : t("cat.book")} <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Positioning strip */}
      <section className="border-y border-white/10 bg-card/30">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="font-serif font-light text-3xl sm:text-4xl text-white leading-tight">{t("pos.title")}</h2>
          <p className="text-muted-foreground mt-4 text-base">{t("pos.subtitle")}</p>
        </div>
      </section>

      {/* Fleet / Bento */}
      <section id="fleet" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <p className="text-xs tracking-luxe uppercase text-muted-foreground mb-4">{t("fleet.eyebrow")}</p>
        <h2 className="font-serif font-light text-4xl sm:text-5xl text-white mb-12 max-w-2xl leading-tight">
          {t("fleet.title")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[240px]">
          <div className="md:col-span-2 md:row-span-2 relative rounded-lg overflow-hidden border border-white/10 group">
            <img src={IMAGES.escaladeExterior} alt="Cadillac Escalade private chauffeur SUV" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-0 p-8">
              <h3 className="font-serif text-3xl text-white">{t("fleet.escalade")}</h3>
              <p className="text-white/70 text-sm mt-2">{t("fleet.escaladeSub")}</p>
            </div>
          </div>
          <div className="relative rounded-lg overflow-hidden border border-white/10 group">
            <img src={IMAGES.escaladeInterior} alt="Cadillac Escalade interior" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-0 p-6">
              <h3 className="font-serif text-xl text-white">{t("fleet.interior")}</h3>
            </div>
          </div>
          <div className="relative rounded-lg overflow-hidden border border-white/10 group">
            <img src={IMAGES.transitBus} alt="Ford Sprinter passenger van" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-0 p-6">
              <h3 className="font-serif text-xl text-white">{t("svc.party_bus.title")}</h3>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="process" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative rounded-lg overflow-hidden border border-white/10 h-[520px]">
            <img src={IMAGES.driving} alt="Professional chauffeur driving" loading="lazy" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/20" />
          </div>
          <div>
            <p className="text-xs tracking-luxe uppercase text-muted-foreground mb-4">{t("process.eyebrow")}</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-white mb-12 leading-tight">
              {t("process.title")}
            </h2>
            <div className="space-y-10">
              {[
                { icon: Car, title: t("process.s1.title"), desc: t("process.s1.desc") },
                { icon: Calendar, title: t("process.s2.title"), desc: t("process.s2.desc") },
                { icon: Users, title: t("process.s3.title"), desc: t("process.s3.desc") },
              ].map((step, i) => (
                <div key={step.title} className="flex gap-6" data-testid={`process-step-${i}`}>
                  <div className="flex-shrink-0 h-12 w-12 rounded-full border border-white/20 flex items-center justify-center">
                    <step.icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white text-lg font-medium mb-1">{step.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 lg:px-10 py-24" data-testid="faq-section">
        <p className="text-xs tracking-luxe uppercase text-muted-foreground mb-4 text-center">{t("faq.eyebrow")}</p>
        <h2 className="font-serif font-light text-4xl sm:text-5xl text-white mb-12 text-center leading-tight">
          {t("faq.title")}
        </h2>
        <Accordion type="single" collapsible className="w-full">
          {[1, 2, 3, 4, 5].map((n) => (
            <AccordionItem key={n} value={`q${n}`} className="border-white/10" data-testid={`faq-item-${n}`}>
              <AccordionTrigger className="text-white text-left hover:no-underline text-base">{t(`faq.q${n}`)}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{t(`faq.a${n}`)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.cityNight} alt="Montréal city at night" loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <h2 className="font-serif font-light text-4xl sm:text-6xl text-white leading-tight">{t("cta.title")}</h2>
          <p className="text-white/70 mt-6 text-lg">{t("cta.subtitle")}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <Button data-testid="cta-book-btn"
              className="rounded-full bg-white text-black hover:bg-white/85 h-14 px-10 text-base"
              onClick={() => navigate("/book")}>
              {t("cta.button")} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <a href={`tel:+1${phone.replace(/[^0-9]/g, "")}`}>
              <Button variant="outline"
                className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 h-14 px-10 text-base w-full">
                <Phone className="h-4 w-4 mr-2" /> {phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
